<template>
  <div class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
    <div class="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <h3 class="text-lg font-medium text-gray-900">
          {{ isEditing ? (show?.status === 'declined' ? 'Edit & Resubmit Show' : 'Edit Show') : 'Create New Show' }}
        </h3>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <!-- Form -->
      <form @submit.prevent="saveShow" class="space-y-6">
        <!-- Show Name -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Show Name <span class="text-red-500">*</span>
          </label>
          <input
            v-model="form.name"
            type="text"
            required
            placeholder="e.g., Trade Show 2024"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            :class="{ 'border-red-500': errors.name }"
          />
          <p v-if="errors.name" class="mt-1 text-sm text-red-600">{{ errors.name }}</p>
        </div>

        <!-- Date Range -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Start Date <span class="text-red-500">*</span>
            </label>
            <input
              v-model="form.start_date"
              type="date"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              :class="{ 'border-red-500': errors.start_date }"
            />
            <p v-if="errors.start_date" class="mt-1 text-sm text-red-600">{{ errors.start_date }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              End Date <span class="text-red-500">*</span>
            </label>
            <input
              v-model="form.end_date"
              type="date"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              :class="{ 'border-red-500': errors.end_date }"
            />
            <p v-if="errors.end_date" class="mt-1 text-sm text-red-600">{{ errors.end_date }}</p>
          </div>
        </div>

        <!-- Tablet Requirements -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Number of Tablets <span class="text-red-500">*</span>
          </label>
          <input
            v-model.number="form.tablet_count"
            type="number"
            min="1"
            max="20"
            required
            placeholder="How many tablets do you need?"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            :class="{ 'border-red-500': errors.tablet_count }"
          />
          <p v-if="errors.tablet_count" class="mt-1 text-sm text-red-600">{{ errors.tablet_count }}</p>
        </div>

        <!-- Special Requirements -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Special Requirements</label>
          <textarea
            v-model="form.notes"
            rows="3"
            placeholder="Any special requirements or notes..."
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          ></textarea>
        </div>

        <!-- Info for declined shows -->
        <div v-if="show?.status === 'declined'" class="bg-blue-50 rounded-lg p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-blue-700">
                <strong>Note:</strong> This show was previously declined. After saving your changes, it will automatically be resubmitted for quote approval.
              </p>
            </div>
          </div>
        </div>

        <!-- Lead Capture URL Preview -->
        <div v-if="showUrlPreview" class="bg-blue-50 rounded-lg p-4">
          <h4 class="text-sm font-medium text-blue-700 mb-2">Lead Capture URL</h4>
          <div class="flex">
            <input
              :value="leadCaptureUrl"
              type="text"
              readonly
              class="flex-1 px-3 py-2 border border-blue-300 rounded-l-md bg-blue-50 text-blue-700 text-sm"
            />
            <button
              @click="copyUrl"
              type="button"
              class="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              Copy
            </button>
          </div>
          <p class="mt-2 text-xs text-blue-600">
            This URL will be available after the show is created.
          </p>
        </div>

        <!-- Actions -->
        <div class="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            @click="$emit('close')"
            type="button"
            class="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="saving"
            class="btn-primary"
          >
            <div v-if="saving" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            {{ saving ? 'Submitting...' : (isEditing ? 'Update Show' : 'Submit Quote Request') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import config from '../config.js'

// Props
const props = defineProps({
  show: {
    type: Object,
    default: null
  }
})

// Emits
const emit = defineEmits(['close', 'saved'])

// Reactive data
const saving = ref(false)
const errors = reactive({})

const form = reactive({
  name: '',
  start_date: '',
  end_date: '',
  tablet_count: 1,
  notes: ''
})

// Computed properties
const isEditing = computed(() => !!props.show)

const showUrlPreview = computed(() => {
  return form.name && form.start_date && form.end_date
})

const leadCaptureUrl = computed(() => {
  if (!showUrlPreview.value) return ''
  
  // This would be the actual URL once the show is created
  // For now, we'll show a placeholder
  const baseUrl = window.location.origin
  const showSlug = form.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, '-')
  return `${baseUrl}/booth/[client-slug]/[show-slug]`
})

// Methods
const validateForm = () => {
  errors.name = ''
  errors.start_date = ''
  errors.end_date = ''
  errors.tablet_count = ''

  if (!form.name.trim()) {
    errors.name = 'Show name is required'
  }

  if (!form.start_date) {
    errors.start_date = 'Start date is required'
  }

  if (!form.end_date) {
    errors.end_date = 'End date is required'
  }

  if (!form.tablet_count || form.tablet_count < 1) {
    errors.tablet_count = 'Number of tablets is required (minimum 1)'
  }

  if (form.start_date && form.end_date) {
    const startDate = new Date(form.start_date)
    const endDate = new Date(form.end_date)
    
    if (endDate < startDate) {
      errors.end_date = 'End date must be after start date'
    }
  }

  return !Object.values(errors).some(error => error)
}

const saveShow = async () => {
  if (!validateForm()) return

  try {
    saving.value = true
    const token = localStorage.getItem('admin_token')
    if (!token) return

    if (isEditing.value) {
      // Editing existing show
      const updateResponse = await fetch(`${config.apiBaseUrl}/admin/shows/${props.show.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: form.name.trim(),
          start_date: form.start_date,
          end_date: form.end_date,
          tablet_count: form.tablet_count,
          notes: form.notes
        })
      })

      if (!updateResponse.ok) {
        const error = await updateResponse.json()
        alert(`Error updating show: ${error.error || 'Unknown error'}`)
        return
      }

      const updateData = await updateResponse.json()
      
      // If this was a declined show, automatically resubmit for quote approval
      if (props.show.status === 'declined') {
        const resubmitResponse = await fetch(`${config.apiBaseUrl}/admin/shows/${props.show.id}/resubmit-quote`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            notes: form.notes
          })
        })

        if (resubmitResponse.ok) {
          const resubmitData = await resubmitResponse.json()
          emit('saved', resubmitData.show)
          alert('Show updated and quote request resubmitted successfully! We will review your updated request.')
        } else {
          const error = await resubmitResponse.json()
          alert(`Show updated but failed to resubmit quote: ${error.error || 'Unknown error'}`)
        }
      } else {
        // Regular show update
        emit('saved', updateData.show)
        alert('Show updated successfully!')
      }
      
    } else {
      // Creating new show
      const createResponse = await fetch(`${config.apiBaseUrl}/admin/shows`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: form.name.trim(),
          start_date: form.start_date,
          end_date: form.end_date,
          tablet_count: form.tablet_count,
          notes: form.notes
        })
      })

      if (!createResponse.ok) {
        const error = await createResponse.json()
        alert(`Error creating show: ${error.error || 'Unknown error'}`)
        return
      }

      const createData = await createResponse.json()
      const showId = createData.show.id

      // Then immediately submit for quote approval
      const quoteResponse = await fetch(`${config.apiBaseUrl}/admin/shows/${showId}/request-quote`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          notes: form.notes
        })
      })

      if (quoteResponse.ok) {
        const quoteData = await quoteResponse.json()
        emit('saved', quoteData.show)
        alert('Quote request submitted successfully! We will review your request and get back to you with pricing.')
      } else {
        const error = await quoteResponse.json()
        alert(`Error submitting quote request: ${error.error || 'Unknown error'}`)
      }
    }
  } catch (error) {
    console.error('Error saving show:', error)
    alert(`Error ${isEditing.value ? 'updating' : 'creating'} show. Please try again.`)
  } finally {
    saving.value = false
  }
}

const copyUrl = async () => {
  try {
    await navigator.clipboard.writeText(leadCaptureUrl.value)
    // You could add a toast notification here
  } catch (error) {
    console.error('Error copying URL:', error)
    // Fallback for older browsers
    const textArea = document.createElement('textarea')
    textArea.value = leadCaptureUrl.value
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
  }
}

// Initialize form data
onMounted(() => {
  if (props.show) {
    // Editing existing show
    form.name = props.show.name || ''
    form.start_date = props.show.start_date || ''
    form.end_date = props.show.end_date || ''
    form.tablet_count = props.show.tablet_count || 1
    form.notes = props.show.notes || ''
  } else {
    // Creating new show - set default dates
    const today = new Date()
    const nextWeek = new Date(today)
    nextWeek.setDate(today.getDate() + 7)
    
    form.start_date = today.toISOString().split('T')[0]
    form.end_date = nextWeek.toISOString().split('T')[0]
  }
})
</script> 